// An engagement model's thresholds, on the fit ladder.
//
// `scoreLadder.ts` and `TieringPanel` are written against the four fit tiers:
// the keys the thresholds are stored under, the segment names in the aria
// labels, the ranges a typed threshold is checked against. An engagement
// model stores the same three cuts under its own names, very high, high and
// medium, in the same order of strength. Rather than teach every ladder
// function a second vocabulary, the page translates at its edge: fit keys
// going in, engagement keys coming out, and the tier outcomes relabelled with
// the names the rest of the product uses for them. Position carries the
// meaning in both vocabularies, which is what makes the translation exact.
import { EnsembleThreshold } from '../../../models/ensembling';
import {
  BOUNDED_TIERS,
  PointsGrid,
  PointsGridRow,
  TierOutcome,
} from './scoreLadder';
import {
  SEGMENTS,
  segmentFill,
  segmentInk,
  segmentLabel,
} from './segmentVocabulary';

/** The three cuts an engagement model stores, best first. */
export const ENGAGEMENT_BOUNDED = ['very high', 'high', 'medium'] as const;

/**
 * The shares auto select picks today: the top tenth, the next fifth, the
 * next fifth, and everyone else (`auto_threshold/point_based/behavioral.ts`).
 * Cumulative, as the ladder's bounds are.
 */
export const AUTO_SELECT_BOUNDS = [0.1, 0.3, 0.5];

/** Stored engagement thresholds as the fit ladder reads them. */
export function toFitKeys(
  thresholds: EnsembleThreshold | null | undefined
): EnsembleThreshold {
  const out: EnsembleThreshold = {};
  ENGAGEMENT_BOUNDED.forEach((segment, index) => {
    out[BOUNDED_TIERS[index]] = Number(thresholds?.[segment]) || 0;
  });
  return out;
}

/** Thresholds the ladder derived, under the names the model stores. */
export function toEngagementKeys(
  thresholds: EnsembleThreshold
): EnsembleThreshold {
  const out: EnsembleThreshold = {};
  BOUNDED_TIERS.forEach((segment, index) => {
    out[ENGAGEMENT_BOUNDED[index]] = Number(thresholds?.[segment]) || 0;
  });
  return out;
}

/** The ladder's fit-named tiers, named and coloured as engagement tiers. */
export function relabelTiers(tiers: TierOutcome[]): TierOutcome[] {
  return tiers.map((tier, index) => {
    const segment = SEGMENTS.behavioral[index];
    return {
      ...tier,
      label: segmentLabel('behavioral', segment),
      color: segmentFill('behavioral', segment),
      textColor: segmentInk('behavioral', segment),
    };
  });
}

/**
 * The grid with every score floored to a whole point, merged. For a whole
 * number t, `floor(score) >= t` exactly when `score >= t`, so this ladder puts
 * the same leads in each tier as the exact one does at any whole threshold:
 * it only removes the fractional rungs a drag could otherwise land on.
 */
export function wholePointGrid(grid: PointsGrid | null): PointsGrid | null {
  if (!grid) return grid;
  const merged = new Map<number, PointsGridRow>();
  grid.rows.forEach((row) => {
    const score = Math.floor(row.score);
    const into = merged.get(score) ?? { score, population: 0, conversions: 0 };
    into.population += row.population;
    into.conversions += row.conversions;
    merged.set(score, into);
  });
  return { ...grid, rows: Array.from(merged.values()) };
}
